import { useState } from "react";
import { deltamathAPI } from "../utils";
import axios from "axios";
import clsx from "clsx";
import { useQuery } from "@tanstack/react-query";
import { LearnerAssignment } from "../../learner/types";

const LearnerProblemSupport = () => {
  const [state, setState] = useState<{
    email?: string;
    courseId?: string;
    unitId?: string;
    subunitId?: string;
    type?: "preQuiz" | "postQuiz" | "practice";
  }>({});

  const { data, error, refetch } = useQuery<LearnerAssignment>({
    queryKey: ["learner_problem_support", state],
    queryFn: async () => {
      const response = await axios.get(
        `${deltamathAPI()}/manager_new/parent-portal/learner_problem_support`,
        { params: { ...state } }
      );
      return response.data;
    },
    enabled: false,
    retry: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    refetch();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="p-6">
      <h1 className="mb-6 text-2xl font-bold">Learner Problem Support</h1>
      <form onSubmit={handleSubmit} className="max-w-md space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={state.email || ""}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="courseId"
            className="block text-sm font-medium text-gray-700"
          >
            Course ID *
          </label>
          <input
            type="text"
            id="courseId"
            name="courseId"
            required
            value={state.courseId || ""}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="unitId"
            className="block text-sm font-medium text-gray-700"
          >
            Unit ID (Optional)
          </label>
          <input
            type="text"
            id="unitId"
            name="unitId"
            value={state.unitId || ""}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="subunitId"
            className="block text-sm font-medium text-gray-700"
          >
            Subunit ID (Optional)
          </label>
          <input
            type="text"
            id="subunitId"
            name="subunitId"
            value={state.subunitId || ""}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="type"
            className={clsx(
              "block text-sm font-medium text-gray-700",
              !state.subunitId && "opacity-50"
            )}
          >
            Type (Optional)
          </label>
          <select
            id="type"
            name="type"
            value={state.type || ""}
            onChange={handleChange}
            className={clsx(
              `mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500`,
              !state.subunitId && "opacity-50"
            )}
            disabled={!state.subunitId}
          >
            <option value="">Select a type</option>
            <option value="preQuiz">Pre Quiz</option>
            <option value="postQuiz">Post Quiz</option>
            <option value="practice">Practice</option>
          </select>
        </div>

        <button
          type="submit"
          className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Submit
        </button>
      </form>

      {error && (
        <div className="mt-10 text-dm-error-500">
          Error: {(error as any)?.response?.data?.message}
        </div>
      )}

      {data && (
        <div className="mt-8 space-y-6">
          <div>
            <h2 className="mb-2 text-lg font-semibold">Skills</h2>
            <div className="max-h-96 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
              <pre className="whitespace-pre-wrap text-sm">
                {JSON.stringify(data.skills, null, 2)}
              </pre>
            </div>
          </div>

          {data.resultHist && (
            <div>
              <h2 className="mb-2 text-lg font-semibold">Result History</h2>
              <div className="max-h-96 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
                <pre className="whitespace-pre-wrap text-sm">
                  {JSON.stringify(data.resultHist, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LearnerProblemSupport;
